import { useEffect, useState } from 'react';
import {
  Modal, Box, Typography, IconButton, Button, Stack, TextField,
  CircularProgress, Alert, Chip, Divider, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import axios from 'api/axiosInstance';
import endpoints from 'api/endpoints';

const DAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

const formatTime = (t) => {
  if (!t) return '-';
  const [h, m] = t.slice(0, 5).split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  return `${(h % 12) || 12}:${String(m).padStart(2, '0')} ${period}`;
};

// Number of online appointment slots a start/end/duration window yields.
const computeSlotCount = (start, end, duration) => {
  if (!start || !end || !duration) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm);
  const dur = Number(duration);
  if (mins <= 0 || !dur) return 0;
  return Math.floor(mins / dur);
};

const EMPTY_FORM = {
  days: [],
  // Step 1: branches this doctor attends (1+). Step 3 assigns a branch per block
  // from this set.
  branches: [],
  // Up to 3 time blocks per day (e.g. 9–11, 12–3, 3–5); each becomes its own
  // availability row with its own branch + walk-in settings. Gaps = breaks.
  blocks: [{ start_time: '09:00', end_time: '17:00', slot_duration: 15, accept_walk_ins: false, walk_in_capacity: '', location_id: '' }],
};

// Wrapper that visually + functionally locks a wizard step until ready.
function StepSection({ index, title, locked, done, children }) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: done ? 'primary.main' : 'divider',
        borderRadius: 1.5,
        p: 1.5,
        opacity: locked ? 0.45 : 1,
        pointerEvents: locked ? 'none' : 'auto',
        transition: 'opacity 0.2s, border-color 0.2s',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, color: done ? 'primary.main' : 'text.secondary', display: 'block', mb: 1 }}>
        Step {index} · {title}{done ? ' ✓' : ''}
      </Typography>
      {children}
    </Box>
  );
}

const outerSx = {
  position: 'absolute', top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 640 },
  maxHeight: '92vh',
  bgcolor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderRadius: 3,
  boxShadow: '0 24px 64px rgba(15, 23, 42, 0.22)',
  border: '1px solid rgba(255, 255, 255, 0.6)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  outline: 'none',
};

const headerSx = {
  flexShrink: 0,
  px: 3, py: 2,
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(12px)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const bodySx = {
  flex: 1,
  overflowY: 'auto',
  px: 3, py: 2.5,
  bgcolor: 'rgba(248, 250, 252, 0.4)',
};

export default function DoctorScheduleModal({ open, onClose, doctor, tenantId }) {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availabilities, setAvailabilities] = useState([]);
  const [locations, setLocations] = useState([]);

  // New-availability form
  const [form, setForm] = useState(EMPTY_FORM);

  // Edit-branch dialog state
  const [editRow, setEditRow] = useState(null); // the availability row being edited
  const [editLocationId, setEditLocationId] = useState('');
  const [editAcceptWalkIns, setEditAcceptWalkIns] = useState(false);
  const [editWalkInCapacity, setEditWalkInCapacity] = useState('');
  const [editImpact, setEditImpact] = useState(null); // { count, sample_dates, current_location_name }
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    if (!open || !doctor?.id) return;
    setError(''); setSuccess('');
    setForm(EMPTY_FORM);
    Promise.all([fetchAvailability(), fetchLocations()]).finally(() => setLoading(false));
  }, [open, doctor?.id]);

  const fetchAvailability = async () => {
    try {
      const resp = await axios.get(endpoints.company.doctorAvailability(doctor.id), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailabilities(Array.isArray(resp.data) ? resp.data : []);
    } catch {
      setAvailabilities([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const resp = await axios.get(endpoints.company.locations(tenantId), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(Array.isArray(resp.data) ? resp.data : (resp.data?.results || []));
    } catch {
      setLocations([]);
    }
  };

  const toggleDay = (d) => {
    setForm((p) => ({
      ...p,
      days: p.days.includes(d) ? p.days.filter((x) => x !== d) : [...p.days, d]
    }));
  };

  // Step 1: branches attended (multi-select). Auto-assign the branch to blocks
  // when exactly one is selected; clear any block branch no longer in the set.
  const toggleBranch = (locId) => setForm((p) => {
    const branches = p.branches.includes(locId) ? p.branches.filter((x) => x !== locId) : [...p.branches, locId];
    const blocks = p.blocks.map((b) => ({
      ...b,
      location_id: branches.includes(b.location_id) ? b.location_id : (branches.length === 1 ? branches[0] : ''),
    }));
    return { ...p, branches, blocks };
  });

  // Time-block editor (multiple sessions per day); each block carries its own
  // branch + walk-in settings.
  const addBlock = () => setForm((p) => ({
    ...p,
    blocks: [...p.blocks, {
      start_time: '', end_time: '', slot_duration: p.blocks[p.blocks.length - 1]?.slot_duration || 15,
      accept_walk_ins: false, walk_in_capacity: '', location_id: p.branches.length === 1 ? p.branches[0] : '',
    }],
  }));
  const removeBlock = (i) => setForm((p) => ({ ...p, blocks: p.blocks.filter((_, idx) => idx !== i) }));
  const updateBlock = (i, field, val) => setForm((p) => ({
    ...p,
    blocks: p.blocks.map((b, idx) => (idx === i ? { ...b, [field]: val } : b)),
  }));

  const handleCreate = async () => {
    setError(''); setSuccess('');
    if (form.days.length === 0) { setError('Pick at least one day'); return; }
    if (form.branches.length === 0) { setError('Pick at least one branch'); return; }
    if (form.blocks.length === 0) { setError('Add at least one time block'); return; }
    if (form.blocks.some((b) => !b.start_time || !b.end_time || b.start_time >= b.end_time)) {
      setError('Each time block needs a start before its end'); return;
    }
    if (form.blocks.some((b) => !b.location_id)) { setError('Assign a branch to every time block'); return; }
    try {
      setSaving(true);
      let createdAny = false;
      // One availability row per (block × day), each with its own branch + walk-in.
      // Different start_times on the same day are separate sessions (gap = break).
      for (const b of form.blocks) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await axios.post(
          endpoints.company.doctorAvailability(doctor.id),
          {
            days: form.days,
            location_id: Number(b.location_id),
            start_time: b.start_time + ':00',
            end_time: b.end_time + ':00',
            slot_duration: Number(b.slot_duration) || 15,
            accept_walk_ins: !!b.accept_walk_ins,
            walk_in_capacity: b.accept_walk_ins ? (Number(b.walk_in_capacity) || 0) : 0,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if ((resp.data?.days_created || []).length > 0) createdAny = true;
      }
      setSuccess(form.blocks.length > 1 ? `${form.blocks.length} sessions added` : 'Schedule added');
      if (createdAny) setForm(EMPTY_FORM);
      fetchAvailability();
    } catch (err) {
      const d = err.response?.data;
      setError(typeof d === 'object' ? Object.values(d).flat().join('. ') : 'Failed to add schedule');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = async (row) => {
    setEditRow(row);
    setEditLocationId(row.location || '');
    setEditAcceptWalkIns(!!row.accept_walk_ins);
    setEditWalkInCapacity(row.walk_in_capacity || '');
    setEditImpact(null);
    setEditLoading(true);
    try {
      const resp = await axios.get(
        endpoints.company.doctorAvailabilityImpact(doctor.id, row.id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditImpact(resp.data);
    } catch {
      setEditImpact({ count: 0, sample_dates: [] });
    } finally {
      setEditLoading(false);
    }
  };

  const submitEdit = async () => {
    if (!editRow) return;
    setEditSaving(true);
    setError('');
    try {
      await axios.patch(
        endpoints.company.doctorAvailabilityRow(doctor.id, editRow.id),
        {
          location_id: editLocationId ? Number(editLocationId) : null,
          accept_walk_ins: editAcceptWalkIns,
          walk_in_capacity: editAcceptWalkIns ? (Number(editWalkInCapacity) || 0) : 0,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditRow(null);
      setSuccess('Schedule updated');
      fetchAvailability();
    } catch (err) {
      const d = err.response?.data;
      setError(typeof d === 'object' ? Object.values(d).flat().join('. ') : 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this schedule entry?')) return;
    try {
      await axios.delete(
        endpoints.company.doctorAvailability(doctor.id),
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { ids: [id] }
        }
      );
      fetchAvailability();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  // Group existing availability by day for display
  const grouped = DAYS.map((d) => ({
    ...d,
    rows: availabilities.filter((a) => a.day_of_week === d.value).sort((a, b) => a.start_time.localeCompare(b.start_time))
  }));

  // How many sessions each day already has. Up to 3 sessions/day are allowed
  // (e.g. 9–11, 12–3, 3–5) — a day is only locked once it has all 3.
  const MAX_SESSIONS_PER_DAY = 3;
  const daySessionCount = availabilities.reduce((acc, a) => {
    acc[a.day_of_week] = (acc[a.day_of_week] || 0) + 1;
    return acc;
  }, {});

  // Wizard step gating
  // Step 1: at least one day AND one branch. Step 2: valid time blocks.
  // Step 3: every block has a branch assigned.
  const step1Done = form.days.length > 0 && form.branches.length > 0;
  const slotCount = form.blocks.reduce((sum, b) => sum + computeSlotCount(b.start_time, b.end_time, b.slot_duration), 0);
  const blocksValid = form.blocks.length > 0 && form.blocks.every((b) => b.start_time && b.end_time && b.start_time < b.end_time && Number(b.slot_duration) > 0);
  const step2Done = step1Done && blocksValid;
  const step3Valid = step2Done && form.blocks.every((b) => !!b.location_id);
  // A day can hold up to MAX_SESSIONS_PER_DAY sessions; cap addable blocks to the
  // remaining room on the fullest selected day (so we never exceed 3/day).
  const maxSelectedDayCount = form.days.reduce((m, d) => Math.max(m, daySessionCount[d] || 0), 0);
  const blocksAllowed = Math.max(1, MAX_SESSIONS_PER_DAY - maxSelectedDayCount);

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={outerSx}>
        {/* ── Sticky Header ── */}
        <Box sx={headerSx}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Schedule – {doctor?.name || ''}</Typography>
            <Typography variant="caption" color="text.secondary">
              Set weekly availability per branch
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><CloseOutlined /></IconButton>
        </Box>

        {/* ── Scrollable body ── */}
        <Box sx={bodySx}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={2.5}>
              {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
              {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

              {/* Existing schedule */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Schedule</Typography>
                {availabilities.length === 0 ? (
                  <Alert severity="info" sx={{ py: 0.5 }}>No schedule set yet. Add one below.</Alert>
                ) : (
                  <Stack spacing={1}>
                    {grouped.filter((g) => g.rows.length > 0).map((g) => (
                      <Box key={g.value} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>{g.label}</Typography>
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          {g.rows.map((r) => (
                            <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip size="small" label={`${formatTime(r.start_time)} – ${formatTime(r.end_time)}`} />
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={r.location_name || 'No branch'}
                                  color={r.location_name ? 'primary' : 'default'}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {r.slot_duration}-min slots
                                </Typography>
                                {r.accept_walk_ins && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    label={`Walk-ins: ${r.walk_in_capacity || 0}`}
                                  />
                                )}
                              </Box>
                              <Box>
                                <IconButton size="small" color="primary" onClick={() => openEdit(r)} title="Change branch">
                                  <EditOutlined />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                                  <DeleteOutlined />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              <Divider />

              {/* Add new — gated 3-step wizard */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Add Schedule</Typography>

                <Stack spacing={1.5}>
                  {/* Step 1 — days + branches attended */}
                  <StepSection index={1} title="Choose day(s) & branches attended" locked={false} done={step1Done}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Days</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {DAYS.map((d) => {
                        const count = daySessionCount[d.value] || 0;
                        const full = count >= MAX_SESSIONS_PER_DAY;
                        const selected = form.days.includes(d.value);
                        return (
                          <Chip
                            key={d.value}
                            label={count > 0 ? `${d.label} · ${count}/${MAX_SESSIONS_PER_DAY}` : d.label}
                            size="small"
                            onClick={full ? undefined : () => toggleDay(d.value)}
                            disabled={full}
                            color={selected ? 'primary' : (count > 0 ? 'secondary' : 'default')}
                            variant={selected ? 'filled' : 'outlined'}
                            sx={{ cursor: full ? 'not-allowed' : 'pointer' }}
                          />
                        );
                      })}
                    </Box>

                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1.5, mb: 0.5 }}>
                      Branches attended (pick one or more)
                    </Typography>
                    {locations.length === 0 ? (
                      <Alert severity="warning" sx={{ py: 0.5 }}>
                        No branches yet — add them in Company Settings → Locations.
                      </Alert>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {locations.map((l) => {
                          const selected = form.branches.includes(l.id);
                          return (
                            <Chip key={l.id} label={`📍 ${l.name}`} onClick={() => toggleBranch(l.id)}
                              color={selected ? 'primary' : 'default'} variant={selected ? 'filled' : 'outlined'}
                              sx={{ cursor: 'pointer' }} />
                          );
                        })}
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      Up to {MAX_SESSIONS_PER_DAY} sessions per day (e.g. 9–11, 12–3, 3–5); the gaps between them are breaks.
                      A day showing {`${MAX_SESSIONS_PER_DAY}/${MAX_SESSIONS_PER_DAY}`} is full — use the ✎ above to edit.
                    </Typography>
                  </StepSection>

                  {/* Step 2 — time blocks (start/end/slot + walk-in per block) */}
                  <StepSection index={2} title="Set time blocks (sessions)" locked={!step1Done} done={step2Done}>
                    <Stack spacing={1.25}>
                      {form.blocks.map((b, i) => {
                        const cnt = computeSlotCount(b.start_time, b.end_time, b.slot_duration);
                        return (
                          <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Session {i + 1}</Typography>
                              {form.blocks.length > 1 && (
                                <IconButton size="small" color="error" onClick={() => removeBlock(i)} title="Remove this block">
                                  <DeleteOutlined />
                                </IconButton>
                              )}
                            </Stack>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr auto' }, gap: 1.5, alignItems: 'start' }}>
                              <TextField size="small" type="time" label="Start" value={b.start_time}
                                onChange={(e) => updateBlock(i, 'start_time', e.target.value)} InputLabelProps={{ shrink: true }} />
                              <TextField size="small" type="time" label="End" value={b.end_time}
                                onChange={(e) => updateBlock(i, 'end_time', e.target.value)} InputLabelProps={{ shrink: true }} />
                              <TextField size="small" type="number" label="Slot (min)" value={b.slot_duration}
                                onChange={(e) => updateBlock(i, 'slot_duration', e.target.value)} inputProps={{ min: 5, max: 120, step: 5 }}
                                sx={{ width: { xs: '100%', sm: 130 }, gridColumn: { xs: '1 / -1', sm: 'auto' } }} helperText="Per-patient duration" />
                            </Box>
                            <Typography variant="caption" color={cnt > 0 ? 'text.secondary' : 'error'} sx={{ display: 'block', mt: 0.5 }}>
                              {cnt > 0 ? `${cnt} slot${cnt === 1 ? '' : 's'}` : 'End must be after start'}
                            </Typography>
                            {/* Walk-in per block */}
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                              <FormControlLabel
                                control={<Switch size="small" checked={b.accept_walk_ins} onChange={(e) => updateBlock(i, 'accept_walk_ins', e.target.checked)} />}
                                label={<Typography variant="caption">Accept walk-ins</Typography>}
                              />
                              {b.accept_walk_ins && (
                                <TextField size="small" type="number" label="Max walk-ins" value={b.walk_in_capacity}
                                  onChange={(e) => { const raw = e.target.value; const v = raw === '' ? '' : Math.min(100, Math.max(0, parseInt(raw, 10) || 0)); updateBlock(i, 'walk_in_capacity', v); }}
                                  placeholder="0" inputProps={{ min: 0, max: 100, step: 1 }} sx={{ width: 120 }} helperText="Cap 100" />
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                      <Button size="small" startIcon={<PlusOutlined />} onClick={addBlock}
                        disabled={form.blocks.length >= blocksAllowed}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}>
                        {form.blocks.length >= blocksAllowed ? `Max ${blocksAllowed} block${blocksAllowed === 1 ? '' : 's'} for the selected day(s)` : 'Add another time block'}
                      </Button>
                    </Stack>
                    <Alert severity={slotCount > 0 ? 'info' : 'warning'} sx={{ mt: 1.5, py: 0.5 }}>
                      {slotCount > 0
                        ? <><strong>{slotCount}</strong> online slot{slotCount === 1 ? '' : 's'} per day across {form.blocks.length} session{form.blocks.length === 1 ? '' : 's'}</>
                        : 'Each block needs an end time after its start'}
                    </Alert>
                  </StepSection>

                  {/* Step 3 — assign a branch to each block (from Step 1 branches) */}
                  <StepSection index={3} title="Assign a branch to each session" locked={!step2Done} done={step3Valid}>
                    {form.branches.length === 0 ? (
                      <Alert severity="info" sx={{ py: 0.5 }}>Pick the branches attended in Step 1 first.</Alert>
                    ) : (
                      <Stack spacing={1}>
                        {form.blocks.map((b, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.25, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>Session {i + 1}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {b.start_time && b.end_time ? `${formatTime(b.start_time + ':00')} – ${formatTime(b.end_time + ':00')}` : 'Set the time in Step 2'}
                              </Typography>
                            </Box>
                            <FormControl size="small" sx={{ minWidth: 180 }} error={!b.location_id}>
                              <InputLabel id={`blk-branch-${i}`}>Branch</InputLabel>
                              <Select labelId={`blk-branch-${i}`} label="Branch" value={b.location_id || ''}
                                onChange={(e) => updateBlock(i, 'location_id', e.target.value)}>
                                {form.branches.map((bid) => {
                                  const loc = locations.find((l) => l.id === bid);
                                  return <MenuItem key={bid} value={bid}>📍 {loc?.name || `Branch ${bid}`}</MenuItem>;
                                })}
                              </Select>
                            </FormControl>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </StepSection>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<PlusOutlined />}
                      onClick={handleCreate}
                      disabled={saving || !step1Done || !step2Done || !step3Valid}
                    >
                      {saving ? 'Adding…' : 'Add Schedule'}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          )}
        </Box>

        {/* ── Edit-branch dialog ── */}
        <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Change Branch</DialogTitle>
          <DialogContent>
            {editLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
            ) : (
              <Stack spacing={2}>
                {editRow && (
                  <Typography variant="body2" color="text.secondary">
                    {DAYS[editRow.day_of_week]?.label} • {formatTime(editRow.start_time)} – {formatTime(editRow.end_time)}
                    {' '}• Currently at <strong>{editImpact?.current_location_name || 'No branch'}</strong>
                  </Typography>
                )}

                {editImpact && editImpact.count > 0 && (
                  <Alert severity="warning">
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {editImpact.count} upcoming booking{editImpact.count === 1 ? '' : 's'} will be affected
                    </Typography>
                    <Typography variant="caption">
                      Patients booked on{' '}
                      {editImpact.sample_dates.slice(0, 3).join(', ')}
                      {editImpact.count > 3 ? ` and ${editImpact.count - 3} more` : ''}
                      {' '}will keep their time but their appointment will be at the new branch.
                    </Typography>
                  </Alert>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    Select new branch
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1 }}>
                    {locations.map((l) => {
                      const selected = Number(editLocationId) === l.id;
                      return (
                        <Box
                          key={l.id}
                          onClick={() => setEditLocationId(l.id)}
                          sx={{
                            cursor: 'pointer', p: 1.25, borderRadius: 1.5,
                            border: '1.5px solid',
                            borderColor: selected ? 'primary.main' : 'divider',
                            bgcolor: selected ? 'primary.50' : 'background.paper',
                            transition: 'all 0.15s', '&:hover': { borderColor: 'primary.light' },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 700, color: selected ? 'primary.main' : 'text.primary', mb: 0.25 }}>
                            📍 {l.name}
                          </Typography>
                          {l.address && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.3 }}>
                              {l.address}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editAcceptWalkIns}
                        onChange={(e) => setEditAcceptWalkIns(e.target.checked)}
                      />
                    }
                    label="Accept walk-in / offline patients"
                  />
                  {editAcceptWalkIns && (
                    <TextField
                      size="small" type="number" label="Max walk-ins"
                      value={editWalkInCapacity}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setEditWalkInCapacity(raw === '' ? '' : Math.min(100, Math.max(0, parseInt(raw, 10) || 0)));
                      }}
                      placeholder="0"
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      sx={{ width: 130 }}
                      helperText="Cap 100"
                    />
                  )}
                </Box>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditRow(null)} disabled={editSaving}>Cancel</Button>
            <Button
              variant="contained"
              onClick={submitEdit}
              disabled={editSaving || !editLocationId}
              color={editImpact?.count > 0 && Number(editLocationId) !== editRow?.location ? 'warning' : 'primary'}
            >
              {editSaving ? 'Updating…' : (editImpact?.count > 0 && Number(editLocationId) !== editRow?.location ? `Update anyway (${editImpact.count} affected)` : 'Update')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Modal>
  );
}
